import React from 'react';
import { Helmet } from 'react-helmet-async';

export default function SEO({ 
  title, 
  description, 
  noIndex = false,
  image = 'https://www.pagehyve.com/og-image.jpg',
  url = 'https://www.pagehyve.com',
  keywords = 'web development, web design, custom websites, e-commerce, React, SEO, Forsyth County Georgia, Bulloch County Georgia, Statesboro, Cumming, Ghana, Accra',
  location = 'Cumming, GA & Accra, Ghana',
  isLocalBusiness = true
}) {
  // Local business schema optimized for Forsyth County, Georgia
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'PageHyve Studios',
    'image': image,
    'description': description,
    'url': url,
    'telephone': '+1 (555) 012-3456',
    'email': 'contact@pagehyve.com',
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': 'Forsyth County',
      'addressLocality': 'Cumming',
      'addressRegion': 'GA',
      'postalCode': '30040',
      'addressCountry': 'US'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '34.0019',
      'longitude': '-84.1227'
    },
    'sameAs': [
      'https://www.facebook.com/pagehyve',
      'https://twitter.com/pagehyve',
      'https://www.linkedin.com/company/pagehyve-studios'
    ],
    'priceRange': '$$',
    'serviceType': ['Web Design', 'Web Development', 'E-commerce', 'SEO', 'Web Applications'],
    'areaServed': [
      {
        '@type': 'State',
        'name': 'Georgia'
      },
      {
        '@type': 'Country',
        'name': 'Ghana'
      },
      'Forsyth County, GA',
      'Bulloch County, GA',
      'Cumming, GA',
      'Statesboro, GA',
      'Alpharetta, GA',
      'Duluth, GA',
      'Johns Creek, GA',
      'Peachtree Corners, GA',
      'Canton, GA',
      'Roswell, GA',
      'Pool, GA',
      'Brooklet, GA',
      'Accra, Ghana',
      'Tema, Ghana',
      'Kumasi, Ghana',
      'Takoradi, Ghana',
      'Sekondi, Ghana',
      'Cape Coast, Ghana'
    ],
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      'opens': '09:00',
      'closes': '18:00'
    }
  };


  // Organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'PageHyve Studios',
    'url': 'https://www.pagehyve.com',
    'logo': 'https://www.pagehyve.com/logo.png',
    'description': 'Professional web development and design agency',
    'sameAs': [
      'https://www.facebook.com/pagehyve',
      'https://twitter.com/pagehyve',
      'https://www.linkedin.com/company/pagehyve-studios'
    ],
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'Customer Service',
      'telephone': '+1 (555) 012-3456',
      'email': 'contact@pagehyve.com'
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {noIndex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}
      <link rel="canonical" href={url} />

      {/* Local SEO Tags */}
      {location && <meta name="geo.placename" content={location} />}
      {location && <meta name="geo.region" content={`US-${location.split(',')[1]?.trim()}`} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="PageHyve Studios" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:creator" content="@pagehyve" />
      
      {/* Additional SEO */}
      <meta name="author" content="PageHyve Studios" />
      <meta name="theme-color" content="#0f172a" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

      {/* Schema Markup for Rich Snippets & Local SEO */}
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      {localBusinessSchema && <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>}
    </Helmet>
  );
}
